import { DocumentNode, useQuery } from '@apollo/client';

/**
 * Pure decision: a page is "stale" (its URL slug no longer resolves to an
 * entity) only once the fresh client query has COMPLETED successfully AND found
 * nothing. While loading, or on error, we fail open (not stale) so a transient
 * network issue never hides a valid page.
 */
export const isStaleSlug = (
  loading: boolean,
  hasError: boolean,
  exists: boolean
): boolean => !loading && !hasError && !exists;

/**
 * Client-side guard for cached ISR pages. After a slug changes, `res.revalidate`
 * cannot turn an already-cached 200 page into a 404 (Next.js Pages Router
 * limitation), so the old URL keeps serving stale HTML. This hook re-checks
 * (network-only) whether the entity for the current URL slug still exists and
 * returns `true` when the page is orphaned, so the caller can render a 404.
 *
 * Runs only on the client (`ssr: false`) — SSR/ISR HTML is unaffected, and the
 * primary old→new edge redirect stays the main mechanism; this is the fallback
 * for users who land on a stale cached page.
 *
 * Pass `skip: true` for preview rendering: a previewed slug is by definition
 * unsaved, so the existence check would always fail and 404 the preview.
 */
export function useStaleSlugGuard<T>(
  query: DocumentNode,
  variables: Record<string, unknown>,
  exists: (data: T | undefined) => boolean,
  skip = false
): boolean {
  const { data, loading, error } = useQuery<T>(query, {
    variables,
    fetchPolicy: 'network-only',
    ssr: false,
    skip,
  });

  if (skip) {
    return false;
  }

  return isStaleSlug(loading, Boolean(error), exists(data));
}
